/* eslint-disable @typescript-eslint/no-explicit-any */
// Moved from apps/web/src/engine (#333), where `any` is permitted; this package
// is stricter. Disabled at the top rather than rewritten: the messaging document is unvalidated JSON from the store — `sanitizeMessaging` is the thing that gives it a type,
// and retyping it inside an access-control change would mix a refactor into a
// move whose whole value is that it changed nothing. Same treatment as
// banepadResolveCourt.ts and activityRegistration.ts.
// ─────────────────────────────────────────────────────────────────────
// MESSAGING — Messenger-style conversations between members, plus the club
// (centre) talking to its members.
//
// Three kinds of conversation:
//   • 'dm'    — two members. Exactly one per pair (dmKey).
//   • 'group' — several members with a title.
//   • 'club'  — the club/centre and ONE member. The club's «send to all» is a
//               fan-out: one message per member conversation, tagged with the
//               same broadcastId so the club can see it as one send.
//
// Pure functions over one immutable MessagingState; the clock and the id
// source are arguments (engine convention), so everything here is testable
// without React or Storage. Persistence lives in state/useMessaging.
// ─────────────────────────────────────────────────────────────────────

export const CLUB_SENDER_ID = 'club';

/** A court pad writes to the centre as `court:<n>` — a sender that is not a member. */
export const COURT_SENDER_PREFIX = 'court:';
export const courtSenderId = (courtId: number | string): string =>
  `${COURT_SENDER_PREFIX}${courtId}`;
/** The court number behind a court sender id, or null for anyone else. */
export function courtOfSender(id: unknown): number | null {
  const s = id == null ? '' : String(id);
  if (!s.startsWith(COURT_SENDER_PREFIX)) return null;
  const n = Number(s.slice(COURT_SENDER_PREFIX.length));
  return Number.isFinite(n) ? n : null;
}

export type ConversationKind = 'dm' | 'group' | 'club' | 'dynamic';

/**
 * Rule behind an automatic club group (engine/messagingGroups): everyone in
 * the club, or everyone on one ELO level. Membership is derived, not stored.
 */
export type DynamicRule =
  { type: 'all'; clubId: string } | { type: 'level'; clubId: string; level: number };

/** One member's messaging preferences (mute per conversation, opt out of a club's automatic groups). */
export interface MemberPrefs {
  muted: string[];
  leftDynamic: string[];
  /** The member switched the whole messaging module off (no send/receive, hidden from pickers and groups). */
  disabled: boolean;
  updatedAt: number;
}

export interface Conversation {
  id: string;
  kind: ConversationKind;
  /** Member ids as strings; a 'club' conversation holds CLUB_SENDER_ID + one member. */
  participantIds: string[];
  /** Group title; null for dm/club (derived from the counterpart). */
  title: string | null;
  createdAt: number;
  createdBy: string;
  /** sentAt of the newest message (createdAt until one exists). */
  lastMessageAt: number;
  /** Per participant: when they last opened the conversation. */
  readAt: Record<string, number>;
  /** Only on kind 'dynamic': the rule that decides who is in it. */
  rule?: DynamicRule;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  sentAt: number;
  /** Set on every copy of a club «send to all». */
  broadcastId?: string;
}

export interface MessagingState {
  conversations: Conversation[];
  messages: Message[];
  /** Per member id — see MemberPrefs. */
  prefs: Record<string, MemberPrefs>;
}

export const MESSAGING_KEY = 'messages_v1';
export const MESSAGE_MAX_LENGTH = 2000;

export type IdSource = () => string;

export function emptyMessaging(): MessagingState {
  return { conversations: [], messages: [], prefs: {} };
}

const sid = (v: unknown) => (v == null ? '' : String(v));
const trimText = (t: unknown) => sid(t).replace(/\r\n/g, '\n').trim().slice(0, MESSAGE_MAX_LENGTH);

/** Bring a stored blob back to a well-formed state (never throws). */
export function sanitizeMessaging(raw: any): MessagingState {
  const out = emptyMessaging();
  const convs: any[] = Array.isArray(raw?.conversations) ? raw.conversations : [];
  const msgs: any[] = Array.isArray(raw?.messages) ? raw.messages : [];
  for (const c of convs) {
    if (!c || !c.id) continue;
    const kind: ConversationKind =
      c.kind === 'group' || c.kind === 'club' || c.kind === 'dynamic' ? c.kind : 'dm';
    const rule = kind === 'dynamic' ? sanitizeRule(c.rule) : null;
    if (kind === 'dynamic' && !rule) continue;
    const participantIds: string[] = Array.isArray(c.participantIds)
      ? [...new Set((c.participantIds as unknown[]).map(sid).filter(Boolean))]
      : [];
    if (kind !== 'dynamic' && participantIds.length < 2) continue;
    out.conversations.push({
      id: sid(c.id),
      kind,
      participantIds,
      title: kind === 'group' ? trimText(c.title) || null : null,
      createdAt: Number(c.createdAt) || 0,
      createdBy: sid(c.createdBy) || participantIds[0] || 'system',
      // A silent automatic group stays at 0 — «Alle» lists it only once it has messages.
      lastMessageAt: Number(c.lastMessageAt) || (kind === 'dynamic' ? 0 : Number(c.createdAt) || 0),
      readAt: Object.fromEntries(
        Object.entries(c.readAt || {}).map(([k, v]) => [sid(k), Number(v) || 0]),
      ),
      ...(rule ? { rule } : {}),
    });
  }
  const convIds = new Set(out.conversations.map((c) => c.id));
  for (const m of msgs) {
    if (!m || !m.id || !convIds.has(sid(m.conversationId))) continue;
    const text = trimText(m.text);
    if (!text) continue;
    out.messages.push({
      id: sid(m.id),
      conversationId: sid(m.conversationId),
      senderId: sid(m.senderId),
      text,
      sentAt: Number(m.sentAt) || 0,
      ...(m.broadcastId ? { broadcastId: sid(m.broadcastId) } : {}),
    });
  }
  out.messages.sort((a, b) => a.sentAt - b.sentAt);
  const prefs: any = raw?.prefs && typeof raw.prefs === 'object' ? raw.prefs : {};
  for (const [k, v] of Object.entries<any>(prefs)) {
    if (!k || !v || typeof v !== 'object') continue;
    out.prefs[sid(k)] = {
      muted: Array.isArray(v.muted)
        ? [...new Set((v.muted as unknown[]).map(sid).filter(Boolean))]
        : [],
      leftDynamic: Array.isArray(v.leftDynamic)
        ? [...new Set((v.leftDynamic as unknown[]).map(sid).filter(Boolean))]
        : [],
      disabled: Boolean(v.disabled),
      updatedAt: Number(v.updatedAt) || 0,
    };
  }
  return out;
}

function sanitizeRule(raw: any): DynamicRule | null {
  const clubId = sid(raw?.clubId);
  if (!clubId) return null;
  if (raw?.type === 'all') return { type: 'all', clubId };
  if (raw?.type === 'level') {
    const level = Number(raw.level);
    if (Number.isInteger(level) && level > 0) return { type: 'level', clubId, level };
  }
  return null;
}

/**
 * Union of two states — used before every write so two devices sharing one
 * stored blob never drop each other's messages (last-writer-wins would).
 * Conversations and messages are unioned by id; readAt keeps the later stamp.
 */
export function mergeMessaging(a: MessagingState, b: MessagingState): MessagingState {
  const convs = new Map<string, Conversation>();
  for (const c of [...a.conversations, ...b.conversations]) {
    const cur = convs.get(c.id);
    if (!cur) {
      convs.set(c.id, { ...c, readAt: { ...c.readAt } });
      continue;
    }
    const readAt = { ...cur.readAt };
    for (const [k, v] of Object.entries(c.readAt)) readAt[k] = Math.max(readAt[k] || 0, v);
    convs.set(c.id, {
      ...cur,
      participantIds: [...new Set([...cur.participantIds, ...c.participantIds])],
      title: cur.title ?? c.title,
      lastMessageAt: Math.max(cur.lastMessageAt, c.lastMessageAt),
      readAt,
      ...(cur.rule || c.rule ? { rule: cur.rule || c.rule } : {}),
    });
  }
  const msgs = new Map<string, Message>();
  for (const m of [...a.messages, ...b.messages]) if (!msgs.has(m.id)) msgs.set(m.id, m);
  const messages = [...msgs.values()].sort(
    (x, y) => x.sentAt - y.sentAt || x.id.localeCompare(y.id),
  );
  // A merged-in message may postdate the conversation stamp it belongs to.
  for (const m of messages) {
    const c = convs.get(m.conversationId);
    if (c && c.lastMessageAt < m.sentAt) convs.set(c.id, { ...c, lastMessageAt: m.sentAt });
  }
  const prefs: Record<string, MemberPrefs> = { ...(a.prefs || {}) };
  for (const [k, v] of Object.entries(b.prefs || {})) {
    if (!prefs[k] || v.updatedAt > prefs[k].updatedAt) prefs[k] = v;
  }
  return { conversations: [...convs.values()], messages, prefs };
}

// ─── lookups ───

/** Order-independent key for a member pair. */
export function dmKey(a: unknown, b: unknown): string {
  return [sid(a), sid(b)].sort().join('|');
}

export function findDm(state: MessagingState, a: unknown, b: unknown): Conversation | null {
  const key = dmKey(a, b);
  return (
    state.conversations.find(
      (c) => c.kind === 'dm' && dmKey(c.participantIds[0], c.participantIds[1]) === key,
    ) || null
  );
}

export function findClubConversation(
  state: MessagingState,
  memberId: unknown,
): Conversation | null {
  const id = sid(memberId);
  return (
    state.conversations.find(
      (c) =>
        c.kind === 'club' &&
        c.participantIds.includes(id) &&
        c.participantIds.includes(CLUB_SENDER_ID),
    ) || null
  );
}

export function conversationById(state: MessagingState, id: unknown): Conversation | null {
  return state.conversations.find((c) => c.id === sid(id)) || null;
}

/** Messages of one conversation, oldest first. */
export function messagesOf(state: MessagingState, conversationId: unknown): Message[] {
  const id = sid(conversationId);
  return state.messages.filter((m) => m.conversationId === id);
}

export function lastMessageOf(state: MessagingState, conversationId: unknown): Message | null {
  const list = messagesOf(state, conversationId);
  return list.length ? list[list.length - 1] : null;
}

/** Everything the member takes part in, newest activity first. */
export function conversationsFor(state: MessagingState, memberId: unknown): Conversation[] {
  const id = sid(memberId);
  return state.conversations
    .filter((c) => c.participantIds.includes(id))
    .sort((a, b) => b.lastMessageAt - a.lastMessageAt || a.id.localeCompare(b.id));
}

/** Messages the reader has not seen: sent by someone else after their readAt. */
export function unreadCount(
  state: MessagingState,
  conversation: Conversation,
  readerId: unknown,
): number {
  const id = sid(readerId);
  const since = conversation.readAt[id] || 0;
  return state.messages.filter(
    (m) => m.conversationId === conversation.id && m.senderId !== id && m.sentAt > since,
  ).length;
}

export function totalUnread(state: MessagingState, memberId: unknown): number {
  return conversationsFor(state, memberId).reduce((n, c) => n + unreadCount(state, c, memberId), 0);
}

// ─── mutations (return a NEW state; never mutate the input) ───

function withConversation(state: MessagingState, conv: Conversation): MessagingState {
  return { ...state, conversations: [...state.conversations, conv] };
}

function updateConversation(
  state: MessagingState,
  id: string,
  patch: (c: Conversation) => Conversation,
): MessagingState {
  return { ...state, conversations: state.conversations.map((c) => (c.id === id ? patch(c) : c)) };
}

/** The dm between two members, creating it if needed. */
export function ensureDm(
  state: MessagingState,
  a: unknown,
  b: unknown,
  now: number,
  nextId: IdSource,
): { state: MessagingState; conversation: Conversation } {
  if (sid(a) === sid(b) || !sid(a) || !sid(b)) throw new Error('A dm needs two different members');
  const existing = findDm(state, a, b);
  if (existing) return { state, conversation: existing };
  const conversation: Conversation = {
    id: nextId(),
    kind: 'dm',
    participantIds: [sid(a), sid(b)],
    title: null,
    createdAt: now,
    createdBy: sid(a),
    lastMessageAt: now,
    readAt: { [sid(a)]: now },
  };
  return { state: withConversation(state, conversation), conversation };
}

/** A group with a title; the creator is always a participant. */
export function createGroup(
  state: MessagingState,
  creatorId: unknown,
  memberIds: unknown[],
  title: unknown,
  now: number,
  nextId: IdSource,
): { state: MessagingState; conversation: Conversation } {
  const ids = [...new Set([sid(creatorId), ...memberIds.map(sid)].filter(Boolean))];
  if (ids.length < 2) throw new Error('A group needs at least two members');
  const conversation: Conversation = {
    id: nextId(),
    kind: 'group',
    participantIds: ids,
    title: trimText(title) || null,
    createdAt: now,
    createdBy: sid(creatorId),
    lastMessageAt: now,
    readAt: { [sid(creatorId)]: now },
  };
  return { state: withConversation(state, conversation), conversation };
}

/** The club's conversation with one member, creating it if needed. */
export function ensureClubConversation(
  state: MessagingState,
  memberId: unknown,
  now: number,
  nextId: IdSource,
  createdBy: string = CLUB_SENDER_ID,
): { state: MessagingState; conversation: Conversation } {
  if (!sid(memberId)) throw new Error('A club conversation needs a member');
  const existing = findClubConversation(state, memberId);
  if (existing) return { state, conversation: existing };
  const conversation: Conversation = {
    id: nextId(),
    kind: 'club',
    participantIds: [CLUB_SENDER_ID, sid(memberId)],
    title: null,
    createdAt: now,
    createdBy,
    lastMessageAt: now,
    readAt: { [createdBy]: now },
  };
  return { state: withConversation(state, conversation), conversation };
}

/** Append one message. The sender has, by definition, read up to now. */
export function appendMessage(
  state: MessagingState,
  conversationId: unknown,
  senderId: unknown,
  text: unknown,
  now: number,
  nextId: IdSource,
  broadcastId?: string,
): { state: MessagingState; message: Message } {
  const conv = conversationById(state, conversationId);
  if (!conv) throw new Error('Unknown conversation');
  if (conv.kind !== 'dynamic' && !conv.participantIds.includes(sid(senderId)))
    throw new Error('Sender is not in the conversation');
  const body = trimText(text);
  if (!body) throw new Error('Empty message');
  const message: Message = {
    id: nextId(),
    conversationId: conv.id,
    senderId: sid(senderId),
    text: body,
    sentAt: now,
    ...(broadcastId ? { broadcastId } : {}),
  };
  const next = updateConversation(state, conv.id, (c) => ({
    ...c,
    lastMessageAt: Math.max(c.lastMessageAt, now),
    readAt: { ...c.readAt, [sid(senderId)]: now },
  }));
  // Keep time order even when devices disagree slightly on the clock.
  const messages = [...next.messages, message].sort((x, y) => x.sentAt - y.sentAt);
  return { state: { ...next, messages }, message };
}

/** The reader opened the conversation. */
export function markRead(
  state: MessagingState,
  conversationId: unknown,
  readerId: unknown,
  now: number,
): MessagingState {
  const conv = conversationById(state, conversationId);
  if (!conv) return state;
  if (conv.kind !== 'dynamic' && !conv.participantIds.includes(sid(readerId))) return state;
  if ((conv.readAt[sid(readerId)] || 0) >= now) return state;
  return updateConversation(state, conv.id, (c) => ({
    ...c,
    readAt: { ...c.readAt, [sid(readerId)]: now },
  }));
}

/**
 * The club sends one text to many members: every member gets it in their own
 * club conversation, all copies sharing a broadcastId.
 */
export function broadcastFromClub(
  state: MessagingState,
  memberIds: unknown[],
  text: unknown,
  now: number,
  nextId: IdSource,
): { state: MessagingState; broadcastId: string; count: number } {
  // Members who switched messaging off receive nothing — not even the club's broadcasts.
  const ids = [...new Set(memberIds.map(sid).filter(Boolean))].filter(
    (id) => !state.prefs?.[id]?.disabled,
  );
  if (!trimText(text)) throw new Error('Empty message');
  if (state.prefs?.[CLUB_SENDER_ID]?.disabled) throw new Error('Meldinger er slått av for klubben');
  const broadcastId = nextId();
  let next = state;
  for (const id of ids) {
    const ensured = ensureClubConversation(next, id, now, nextId);
    next = appendMessage(
      ensured.state,
      ensured.conversation.id,
      CLUB_SENDER_ID,
      text,
      now,
      nextId,
      broadcastId,
    ).state;
  }
  return { state: next, broadcastId, count: ids.length };
}

// ─── presentation helpers ───

export interface NameSource {
  /** Member id → display name ('' when unknown). */
  nameOf: (id: string) => string;
  clubName: string;
}

/** «Hanne Bakken» for a dm, the club for a club chat, the title (or the members) for a group. */
export function conversationTitle(
  conv: Conversation,
  viewerId: unknown,
  names: NameSource,
): string {
  const me = sid(viewerId);
  if (conv.kind === 'club') {
    if (me === CLUB_SENDER_ID) {
      const other = conv.participantIds.find((p) => p !== CLUB_SENDER_ID) || '';
      return names.nameOf(other) || 'Ukjent medlem';
    }
    return names.clubName || 'Klubben';
  }
  if (conv.kind === 'dynamic') return conv.title || 'Gruppe';
  if (conv.kind === 'group' && conv.title) return conv.title;
  const others = conv.participantIds
    .filter((p) => p !== me)
    .map((p) => names.nameOf(p) || 'Ukjent');
  return others.join(', ') || 'Samtale';
}

/** Short text for the list row: «Du: …» or «Hanne: …» (group) or just the text. */
export function previewOf(
  state: MessagingState,
  conv: Conversation,
  viewerId: unknown,
  names: NameSource,
): string {
  const last = lastMessageOf(state, conv.id);
  if (!last)
    return conv.kind === 'group'
      ? 'Ny gruppe'
      : conv.kind === 'dynamic'
        ? 'Automatisk gruppe · ingen meldinger ennå'
        : 'Ingen meldinger ennå';
  const me = sid(viewerId);
  const oneLine = last.text.replace(/\s+/g, ' ');
  if (last.senderId === me) return `Du: ${oneLine}`;
  if (conv.kind === 'group' || conv.kind === 'dynamic') {
    const first = (names.nameOf(last.senderId) || 'Ukjent').split(' ')[0];
    return `${first}: ${oneLine}`;
  }
  return oneLine;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const WEEKDAYS = ['søn.', 'man.', 'tir.', 'ons.', 'tor.', 'fre.', 'lør.'];
const MONTHS = [
  'jan.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'jun.',
  'jul.',
  'aug.',
  'sep.',
  'okt.',
  'nov.',
  'des.',
];

/** Messenger-style stamp: «14:32» today, «i går», «ons.» inside a week, «9. sep.» otherwise (+ year if not this year). */
export function timeLabel(ms: number, nowMs: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date(nowMs);
  if (sameDay(d, now)) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'i går';
  const days =
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) /
    86400000;
  if (days > 0 && days < 7) return WEEKDAYS[d.getDay()];
  const base = `${d.getDate()}. ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/** Day separator text for a thread: «I dag», «I går», «Onsdag 9. september». */
export function dayLabel(ms: number, nowMs: number): string {
  const d = new Date(ms);
  const now = new Date(nowMs);
  if (sameDay(d, now)) return 'I dag';
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'I går';
  const wd = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'][d.getDay()];
  const mo = [
    'januar',
    'februar',
    'mars',
    'april',
    'mai',
    'juni',
    'juli',
    'august',
    'september',
    'oktober',
    'november',
    'desember',
  ][d.getMonth()];
  const year = d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`;
  return `${wd} ${d.getDate()}. ${mo}${year}`;
}

export interface ThreadGroup {
  dayKey: string;
  label: string;
  messages: Message[];
}

/** Messages grouped per local day, oldest first, for rendering a thread. */
export function groupByDay(messages: Message[], nowMs: number): ThreadGroup[] {
  const out: ThreadGroup[] = [];
  for (const m of [...messages].sort((a, b) => a.sentAt - b.sentAt)) {
    const d = new Date(m.sentAt);
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    let g = out[out.length - 1];
    if (!g || g.dayKey !== key) {
      g = { dayKey: key, label: dayLabel(m.sentAt, nowMs), messages: [] };
      out.push(g);
    }
    g.messages.push(m);
  }
  return out;
}

/** «HH:MM» for a bubble. */
export function clockLabel(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** The club's sent broadcasts, newest first: one row per broadcastId. */
export function broadcastsOf(
  state: MessagingState,
): { broadcastId: string; text: string; sentAt: number; count: number }[] {
  const map = new Map<
    string,
    { broadcastId: string; text: string; sentAt: number; count: number }
  >();
  for (const m of state.messages) {
    if (!m.broadcastId || m.senderId !== CLUB_SENDER_ID) continue;
    const cur = map.get(m.broadcastId);
    if (cur) cur.count += 1;
    else
      map.set(m.broadcastId, {
        broadcastId: m.broadcastId,
        text: m.text,
        sentAt: m.sentAt,
        count: 1,
      });
  }
  return [...map.values()].sort((a, b) => b.sentAt - a.sentAt);
}
